import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Modal, Form, InputNumber, DatePicker, Upload, Space,
  message, Empty, Tag,
} from 'antd';
import { UploadOutlined, DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import moment from 'moment';
import { fiscalDataService } from '../../services/api';

/**
 * Líneas de captura de la empresa seleccionada: consulta por periodo y carga.
 *
 * La carga se hace en nombre del cliente. El alcance lo valida el backend
 * DENTRO del service, no solo en la ruta.
 */
const CaptureLinesPanel = ({ empresa }) => {
  const [lineas, setLineas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalAbierto, setModalAbierto] = useState(false);
  const [subiendo, setSubiendo] = useState(false);
  const [archivo, setArchivo] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [empresa?.id]);

  const cargar = async () => {
    if (!empresa?.id) return;
    setLoading(true);
    try {
      const data = await fiscalDataService.getCaptureLines(empresa.id);
      setLineas(data?.data?.items || data?.items || []);
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudieron cargar las líneas de captura');
    } finally {
      setLoading(false);
    }
  };

  const descargar = async (period) => {
    try {
      const response = await fiscalDataService.downloadCaptureLine(empresa.id, period);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `linea-captura-${empresa.tax_id}-${period}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error('No se pudo descargar el PDF');
    }
  };

  const subir = async (values) => {
    if (!archivo) {
      message.warning('Adjunta el PDF de la línea de captura');
      return;
    }

    setSubiendo(true);
    try {
      await fiscalDataService.uploadCaptureLine({
        userId: empresa.user_id,
        rfc: empresa.tax_id,
        amount: values.amount,
        period: values.period.format('YYYY-MM'),
        pdf: archivo,
      });
      message.success('Línea de captura cargada');
      setModalAbierto(false);
      setArchivo(null);
      form.resetFields();
      await cargar();
    } catch (err) {
      message.error(err?.response?.data?.error || 'No se pudo cargar la línea de captura');
    } finally {
      setSubiendo(false);
    }
  };

  const columns = [
    {
      title: 'Periodo',
      dataIndex: 'period',
      key: 'period',
      render: (p) => <Tag color="blue">{p}</Tag>,
    },
    {
      title: 'Importe',
      dataIndex: 'amount',
      key: 'amount',
      render: (a) => `$${Number(a).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`,
    },
    {
      title: 'Cargada por',
      dataIndex: 'created_by',
      key: 'created_by',
      // Distinto de user_id: quién la subió, no de quién es.
      render: (v) => (v ? <Tag>{v}</Tag> : <Tag color="default">histórica</Tag>),
    },
    {
      title: '',
      key: 'accion',
      render: (_, linea) => (
        <Button size="small" icon={<DownloadOutlined />} onClick={() => descargar(linea.period)}>
          PDF
        </Button>
      ),
    },
  ];

  return (
    <Card
      title="Líneas de captura"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalAbierto(true)}>
          Cargar línea
        </Button>
      }
    >
      <Table
        rowKey="id"
        loading={loading}
        dataSource={lineas}
        columns={columns}
        pagination={false}
        size="small"
        scroll={{ x: true }}
        locale={{ emptyText: <Empty description="Sin líneas de captura" image={Empty.PRESENTED_IMAGE_SIMPLE} /> }}
      />

      <Modal
        title={`Cargar línea de captura — ${empresa?.tax_id}`}
        open={modalAbierto}
        onCancel={() => setModalAbierto(false)}
        onOk={() => form.submit()}
        confirmLoading={subiendo}
        okText="Cargar"
        cancelText="Cancelar"
      >
        <Form form={form} layout="vertical" onFinish={subir}>
          <Form.Item name="period" label="Periodo" rules={[{ required: true, message: 'Requerido' }]}>
            <DatePicker picker="month" style={{ width: '100%' }} disabledDate={(d) => d && d > moment()} />
          </Form.Item>
          {/* min={0} y NO 0.01: una declaración en ceros o a favor se presenta
              igual y su acuse existe. Con el mínimo en 0.01, rc-input-number
              reajustaba el 0 al salir del campo y registraba un pago de un
              centavo que el contribuyente no debe. */}
          <Form.Item
            name="amount"
            label="Importe a pagar"
            rules={[{ required: true, message: 'Requerido' }]}
            extra="Captura 0 si la declaración quedó en ceros o a favor: se registra igual, con su acuse."
          >
            <InputNumber min={0} step={0.01} precision={2} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item label="PDF" required>
            <Upload
              accept="application/pdf"
              maxCount={1}
              beforeUpload={(file) => { setArchivo(file); return false; }}
              onRemove={() => setArchivo(null)}
              fileList={archivo ? [archivo] : []}
            >
              <Button icon={<UploadOutlined />}>Seleccionar PDF</Button>
            </Upload>
          </Form.Item>
          <Space direction="vertical" size={0}>
            <span style={{ fontSize: 12, color: '#888' }}>
              La línea quedará registrada a nombre del contribuyente, contigo como autor de la carga.
            </span>
          </Space>
        </Form>
      </Modal>
    </Card>
  );
};

export default CaptureLinesPanel;
